import { ErrorMessage } from '@hookform/error-message'
import { IconButton } from '@material-ui/core'
import Backdrop from '@material-ui/core/Backdrop'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Grid from '@material-ui/core/Grid'
import Modal from '@material-ui/core/Modal'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import AddIcon from '@material-ui/icons/Add'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import { nanoid } from '@reduxjs/toolkit'
import { DropzoneDialog } from 'material-ui-dropzone'
import { useEffect, useId, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import config from 'src/aws-exports'
import CountriesAutoComplete from 'src/components/ui/AutoComplete/CountriesAutoComplete'
import CurrencyAutoComplete from 'src/components/ui/AutoComplete/CurrencyAutoComplete'
import {
	setIsImageUploadOpen,
	updateCompanyInfo,
} from 'src/redux/slices/settings'
import { Auth, Storage } from 'src/utils/amplify/legacy'
import { useFetchCompanyByIdQuery } from '@/redux/api/graphql/companyApi'
import { useFetchUserInfoQuery } from '@/redux/api/graphql/userInfoApi'
import LoadingButton from '../../Button/LoadingButton'

const {
	aws_user_files_s3_bucket_region: region,
	aws_user_files_s3_bucket: bucket,
} = config

const useStyles = makeStyles((theme) => ({
	saveModal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	saveModalContent: {
		maxHeight: '80%',
		overflowY: 'scroll',
		borderRadius: '10px',
		backgroundColor: theme.palette.background.paper,
		width: '70%',
	},
	formContainer: {
		padding: theme.spacing(3),
		'& h5': {
			marginBottom: '1.2rem',
		},
	},
	buttonsContainer: {
		flexGrow: 1,
		marginLeft: 'auto',
	},
	button: {
		padding: theme.spacing(1),
	},
	errorMsg: {
		color: 'red',
		padding: 0,
		'&:before': {
			display: 'inline',
			content: '"⚠"',
		},
	},
	fields: {
		display: 'flex',
		'& div': {
			marginRight: '1rem',
		},
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}))

const hasBankAccountInfo = (companyInfo, fields) => {
	return fields.some((field) => {
		const value = companyInfo?.[field]
		return typeof value === 'string' ? value.trim() : Boolean(value)
	})
}

function AccountSettingsModal({ isOpen, closeAccountSettingsModal }) {
	const classes = useStyles()
	const { register, handleSubmit, errors, control } = useForm({ mode: 'all' })
	const { t: tSettings } = useTranslation('settings')
	const { t: tProjectChildren } = useTranslation('projectChildren')
	const { t: tClientFormModal } = useTranslation('ClientFormModal')
	const dispatch = useDispatch()
	const isImageUploadOpen = useSelector(
		(state) => state.settings.isImageUploadOpen
	)
	const [companyLogoUrl, setCompanyLogoUrl] = useState()
	const updateCompanyInfoApiStatus = useSelector(
		(state) => state.settings.updateCompanyInfoApiStatus
	)
	const userInfoId = useSelector((state) => state.auth.userInfo?.id)
	const { data: userInfo } = useFetchUserInfoQuery(
		{
			userInfoId: userInfoId,
		},
		{
			skip: !userInfoId,
		}
	)
	const currentCompanyTenantId = userInfo?.currentCompanyTenantId
	const inFreelancerArchive =
		userInfo?.tenantId === userInfo?.currentCompanyTenantId &&
		userInfo?.cognitoGroup?.includes('freelancer')
	const { data: companyInfo, isLoading: isLoadingCompanyInfo } =
		useFetchCompanyByIdQuery(currentCompanyTenantId, {
			skip: !currentCompanyTenantId || inFreelancerArchive,
		})
	const nameId = useId()
	const emailId = useId()
	const phoneId = useId()
	const websiteId = useId()
	const registrationNumberId = useId()
	const vatNumberId = useId()
	const addressLine1Id = useId()
	const addressLine2Id = useId()
	const bankProfileNameId = useId()
	const bankId = useId()
	const ibanId = useId()
	const bicId = useId()
	const accountNameId = useId()
	const addBankButton2Id = useId()
	const addBankButton3Id = useId()
	const bankProfileName2Id = useId()
	const bank2Id = useId()
	const iban2Id = useId()
	const bic2Id = useId()
	const accountName2Id = useId()
	const bankProfileName3Id = useId()
	const bank3Id = useId()
	const iban3Id = useId()
	const bic3Id = useId()
	const accountName3Id = useId()
	const addBankButton4Id = useId()
	const bankProfileName4Id = useId()
	const bank4Id = useId()
	const iban4Id = useId()
	const bic4Id = useId()
	const accountName4Id = useId()
	const addBankButton5Id = useId()
	const bankProfileName5Id = useId()
	const bank5Id = useId()
	const iban5Id = useId()
	const bic5Id = useId()
	const accountName5Id = useId()

	// biome-ignore lint: Fetching signed S3 URL for company logo on mount and when company info changes
	useEffect(() => {
		const getComponyLogoUrl = async () => {
			let signedUrl = null
			console.log('companyInfo: ', companyInfo)
			if (companyInfo?.logoImage?.length > 0) {
				// If key exists only then attempt to get the logo url else set that as null
				signedUrl = await Storage.get(companyInfo?.logoImage[0]?.key, {
					level: 'protected',
					identityId: companyInfo?.logoImage[0]?.identityId,
					expires: 60 * 60 * 12,
				})
			}

			console.log('signedUrl: ', signedUrl)
			setCompanyLogoUrl(signedUrl)
		}
		if (companyInfo?.logoImage) {
			getComponyLogoUrl()
		}
	}, [companyInfo])

	const userProfileSubmit = (data) => {
		dispatch(updateCompanyInfo(data))
	}

	const handleCloseModal = () => {
		closeAccountSettingsModal()
	}
	const handleImageUploadOpen = () => {
		dispatch(setIsImageUploadOpen(true))
	}
	const handleImageUploadClose = () => {
		dispatch(setIsImageUploadOpen(false))
	}
	const handleImageUploadSubmit = async (files) => {
		// We requeire the users IdentityId from cognito and save it in dynamodb when we upload images
		const cred = await Auth.currentCredentials()

		if (files?.length) {
			// delete old companyLogoUrl
			companyLogoUrl &&
				(await Storage.remove(companyInfo?.logoImage[0]?.key))
			const { name: fileName, type: mimeType } = files[0]
			const key = `${nanoid()}__${fileName
				.trim()
				.replace(/[\n\r\s\t]+/g, '_')}`
			const fileForUpload = {
				bucket,
				key,
				region,
				identityId: cred.identityId,
			}
			try {
				await Storage.put(key, files[0], {
					level: 'protected',
					contentType: mimeType,
				})
				const logoImage = []
				logoImage.push(fileForUpload)
				dispatch(updateCompanyInfo({ logoImage }))
			} catch (err) {}
		}
		dispatch(setIsImageUploadOpen(false))
	}

	const [addBank, setAddBank] = useState({
		addBankButton2: false,
		addBankButton3: false,
		addBankButton4: false,
		addBankButton5: false,
	})
	const handleAddBank = (name) => {
		setAddBank({
			...addBank,
			[name]: true,
		})
	}
	const hasBankTwoInfo = hasBankAccountInfo(companyInfo, [
		'bankProfileName2',
		'bank2',
		'iban2',
		'bic2',
		'accountName2',
	])
	const hasBankThreeInfo = hasBankAccountInfo(companyInfo, [
		'bankProfileName3',
		'bank3',
		'iban3',
		'bic3',
		'accountName3',
	])
	const hasBankFourInfo = hasBankAccountInfo(companyInfo, [
		'bankProfileName4',
		'bank4',
		'iban4',
		'bic4',
		'accountName4',
	])
	const hasBankFiveInfo = hasBankAccountInfo(companyInfo, [
		'bankProfileName5',
		'bank5',
		'iban5',
		'bic5',
		'accountName5',
	])

	return (
		<>
			{isLoadingCompanyInfo ? (
				<Backdrop
					className={classes.backdrop}
					open={isLoadingCompanyInfo}
				>
					<CircularProgress color="inherit" />
				</Backdrop>
			) : (
				<Modal
					aria-labelledby="Account Settings Modal"
					aria-describedby="Add or Edit Account details"
					open={isOpen}
					className={classes.saveModal}
					onBackdropClick={handleCloseModal}
					onEscapeKeyDown={handleCloseModal}
				>
					<div className={classes.saveModalContent}>
						<Grid container>
							<Paper className={classes.formContainer}>
								<form
									onSubmit={handleSubmit(userProfileSubmit)}
									noValidate
								>
									<Grid item container spacing={1}>
										<Grid item xs={12}>
											<Typography variant="h5">
												{tSettings('accountSettings')}
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<Grid item xs={12}>
												<Typography
													style={{
														marginBottom: '1.5rem',
													}}
												>
													{tSettings('logo')}
												</Typography>
											</Grid>
											<Grid item container xs={12}>
												<Grid>
													{companyLogoUrl && (
														<img
															style={{
																height: '50px',
															}}
															src={companyLogoUrl}
															alt="Company Logo"
															//* if the image is included in the HTML first, then requested by JavaScript (react-pdf), it can fail due to Chrome caching.
															// *The second request (from JS) fails because Chrome uses the cached response from the first request
															// * (from HTML), which has no Access-Control-Allow-Origin response header.
															// * Hence setting crossorigin as anonymous
															crossorigin="anonymous"
														/>
													)}
												</Grid>
												<Grid>
													<div>
														<IconButton
															onClick={
																handleImageUploadOpen
															}
															color="primary"
															aria-label="upload picture"
															component="span"
														>
															<PhotoCameraIcon />
														</IconButton>
													</div>
												</Grid>
											</Grid>
											<Grid item xs={12}>
												<Typography variant="body2">
													{tSettings('logoText')}
												</Typography>
											</Grid>
											<DropzoneDialog
												dialogTitle={tSettings(
													'uploadLogoTitle'
												)}
												dropzoneText={tSettings(
													'uploadLogoDropzoneText'
												)}
												cancelButtonText={tSettings(
													'uploadLogoCancel'
												)}
												submitButtonText={tSettings(
													'uploadLogoSubmit'
												)}
												open={isImageUploadOpen}
												onSave={handleImageUploadSubmit}
												acceptedFiles={[
													'image/jpeg',
													'image/png',
												]}
												maxFileSize={5000000}
												filesLimit={1}
												onClose={handleImageUploadClose}
												showPreviewsInDropzone={true}
												showPreviews={false}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register({
													required: true,
												})}
												required
												fullWidth
												id={nameId}
												label={tSettings('name')}
												name="name"
												defaultValue={
													companyInfo?.name
														? companyInfo?.name
														: null
												}
												helperText={
													errors?.name
														? errors.name.type ===
																'required' && (
																<span
																	className={
																		classes.errorMsg
																	}
																>
																	{tSettings(
																		'errors.name'
																	)}
																</span>
															)
														: tSettings('name')
												}
												autoFocus
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register({
													required: true,
												})}
												required
												fullWidth
												id={emailId}
												label={tSettings('email')}
												name="email"
												defaultValue={
													companyInfo?.email
														? companyInfo.email
														: null
												}
												helperText={
													errors.email
														? errors.email.type ===
																'required' && (
																<span
																	className={
																		classes.errorMsg
																	}
																>
																	{tSettings(
																		'errors.email'
																	)}
																</span>
															)
														: tSettings('email')
												}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={phoneId}
												label={tSettings('phone')}
												name="phone"
												defaultValue={
													companyInfo?.phone
														? companyInfo.phone
														: null
												}
												helperText={tSettings('phone')}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={websiteId}
												label={tSettings('website')}
												name="website"
												defaultValue={
													companyInfo?.website
														? companyInfo.website
														: null
												}
												helperText={tSettings(
													'website'
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={registrationNumberId}
												label={tSettings(
													'registrationNumber'
												)}
												name="registrationNumber"
												defaultValue={
													companyInfo?.registrationNumber
														? companyInfo.registrationNumber
														: null
												}
												helperText={tSettings(
													'registrationNumber'
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={vatNumberId}
												label={tSettings('vatNumber')}
												name="vatNumber"
												defaultValue={
													companyInfo?.vatNumber
														? companyInfo.vatNumber
														: null
												}
												helperText={tSettings(
													'vatNumber'
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={addressLine1Id}
												label={tSettings('address1')}
												name="addressLine1"
												defaultValue={
													companyInfo?.addressLine1
														? companyInfo.addressLine1
														: null
												}
												helperText={tSettings(
													'address1'
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={addressLine2Id}
												label={tSettings('address2')}
												name="addressLine2"
												defaultValue={
													companyInfo?.addressLine2
														? companyInfo.addressLine2
														: null
												}
												helperText={tSettings(
													'address2'
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<Controller
												control={control}
												name="currency"
												fullWidth
												size="large"
												defaultValue={
													companyInfo?.accountCurrency
														? companyInfo.accountCurrency
														: ''
												}
												rules={{
													required: true,
												}}
												errors={errors.currency}
												render={(props) => {
													return (
														<CurrencyAutoComplete
															props={props}
															required={true}
															handleChange={
																props.onChange
															}
															errors={errors}
														/>
													)
												}}
											/>
										</Grid>
										<Grid item xs={6}>
											<Controller
												control={control}
												name="country"
												fullWidth
												size="large"
												defaultValue={
													companyInfo?.country
														? companyInfo.country
														: ''
												}
												rules={{
													required: true,
												}}
												errors={errors.country}
												render={(props) => {
													return (
														<CountriesAutoComplete
															props={props}
															required={true}
															handleChange={
																props.onChange
															}
															smallSize={false}
														/>
													)
												}}
											/>
											<ErrorMessage
												errors={errors}
												name="country"
												render={() => (
													<p
														style={{
															color: 'red',
														}}
													>
														{tProjectChildren(
															'addQuoteModal.sections.sectionTwoPhotoGrid.errorMsg.required'
														)}
													</p>
												)}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={bankProfileNameId}
												label={tSettings(
													'bankProfileName'
												)}
												name="bankProfileName"
												defaultValue={
													companyInfo?.bankProfileName
														? companyInfo.bankProfileName
														: 'Bank Profile'
												}
												// helperText={tSettings('bank')}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={bankId}
												label={tSettings('bank')}
												name="bank"
												defaultValue={
													companyInfo?.bank
														? companyInfo.bank
														: null
												}
												// helperText={tSettings('bank')}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={ibanId}
												label={tSettings('iban')}
												name="iban"
												defaultValue={
													companyInfo?.iban
														? companyInfo.iban
														: null
												}
												// helperText={tSettings('iban')}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={bicId}
												label={tSettings('bic')}
												name="bic"
												defaultValue={
													companyInfo?.bic
														? companyInfo.bic
														: null
												}
												// helperText={tSettings('bic')}
											/>
										</Grid>
										<Grid item xs={6}>
											<TextField
												variant="outlined"
												margin="normal"
												inputRef={register}
												fullWidth
												id={accountNameId}
												label={tSettings('accountName')}
												name="accountName"
												defaultValue={
													companyInfo?.accountName
														? companyInfo.accountName
														: null
												}
												// helperText={tSettings('accountName')}
											/>
										</Grid>
										<Grid item xs={6} />
										<Grid item xs={12}>
											<TextField
												id={'termsAndCondition1'}
												name={'termsAndCondition1'}
												label={tSettings(
													'termsAndCondition1'
												)}
												defaultValue={
													companyInfo?.termsAndCondition1
												}
												inputRef={register({
													maxLength: 5000,
												})}
												variant="outlined"
												multiline
												fullWidth
												rows={5}
											/>
											<ErrorMessage
												errors={errors}
												name={'termsAndCondition1'}
												render={() => (
													<p
														style={{
															color: 'red',
														}}
													>
														{tProjectChildren(
															'addQuoteModal.sections.sectionOne.errorMsg.maxLength'
														)}
													</p>
												)}
											/>
										</Grid>
										<Grid item xs={12}>
											<TextField
												id={'termsAndCondition2'}
												name={'termsAndCondition2'}
												label={tSettings(
													'termsAndCondition2'
												)}
												defaultValue={
													companyInfo?.termsAndCondition2
												}
												inputRef={register({
													maxLength: 5000,
												})}
												variant="outlined"
												multiline
												fullWidth
												rows={5}
											/>
											<ErrorMessage
												errors={errors}
												name={'termsAndCondition2'}
												render={() => (
													<p
														style={{
															color: 'red',
														}}
													>
														{tProjectChildren(
															'addQuoteModal.sections.sectionOne.errorMsg.maxLength'
														)}
													</p>
												)}
											/>
										</Grid>
										{!addBank.addBankButton2 &&
										!hasBankTwoInfo ? (
											<Grid
												item
												xs={12}
												style={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<Button
													type="button"
													className={classes.button}
													onClick={() =>
														handleAddBank(
															'addBankButton2'
														)
													}
													startIcon={<AddIcon />}
													id={addBankButton2Id}
												>
													{tSettings('addBank2')}
												</Button>
											</Grid>
										) : (
											<>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bankProfileName2Id}
														label={tSettings(
															'bankProfileName2'
														)}
														name="bankProfileName2"
														defaultValue={
															companyInfo?.bankProfileName2
																? companyInfo.bankProfileName2
																: 'Bank Profile 2'
														}
														// helperText={tSettings('bank')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bank2Id}
														label={`${tSettings(
															'bank'
														)} 2`}
														name="bank2"
														defaultValue={
															companyInfo?.bank2
																? companyInfo.bank2
																: null
														}
														// helperText={tSettings('bank')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={iban2Id}
														label={`${tSettings(
															'iban'
														)} 2`}
														name="iban2"
														defaultValue={
															companyInfo?.iban2
																? companyInfo.iban2
																: null
														}
														// helperText={tSettings('iban')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bic2Id}
														label={`${tSettings(
															'bic'
														)} 2`}
														name="bic2"
														defaultValue={
															companyInfo?.bic2
																? companyInfo.bic2
																: null
														}
														// helperText={tSettings('bic')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={accountName2Id}
														label={`${tSettings(
															'accountName'
														)} 2`}
														name="accountName2"
														defaultValue={
															companyInfo?.accountName2
																? companyInfo.accountName2
																: null
														}
														// helperText={tSettings('accountName')}
													/>
												</Grid>
												<Grid item xs={6} />
											</>
										)}
										{!addBank.addBankButton3 &&
										!hasBankThreeInfo ? (
											<Grid
												item
												xs={12}
												style={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<Button
													type="button"
													className={classes.button}
													onClick={() =>
														handleAddBank(
															'addBankButton3'
														)
													}
													startIcon={<AddIcon />}
													id={addBankButton3Id}
												>
													{tSettings('addBank3')}
												</Button>
											</Grid>
										) : (
											<>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bankProfileName3Id}
														label={tSettings(
															'bankProfileName3'
														)}
														name="bankProfileName3"
														defaultValue={
															companyInfo?.bankProfileName3
																? companyInfo.bankProfileName3
																: 'Bank Profile 3'
														}
														// helperText={tSettings('bank')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bank3Id}
														label={`${tSettings(
															'bank'
														)} 3`}
														name="bank3"
														defaultValue={
															companyInfo?.bank3
																? companyInfo.bank3
																: null
														}
														// helperText={tSettings('bank')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={iban3Id}
														label={`${tSettings(
															'iban'
														)} 3`}
														name="iban3"
														defaultValue={
															companyInfo?.iban3
																? companyInfo.iban3
																: null
														}
														// helperText={tSettings('iban')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bic3Id}
														label={`${tSettings(
															'bic'
														)} 3`}
														name="bic3"
														defaultValue={
															companyInfo?.bic3
																? companyInfo.bic3
																: null
														}
														// helperText={tSettings('bic')}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={accountName3Id}
														label={`${tSettings(
															'accountName'
														)} 3`}
														name="accountName3"
														defaultValue={
															companyInfo?.accountName3
																? companyInfo.accountName3
																: null
														}
														// helperText={tSettings('accountName')}
													/>
												</Grid>
											</>
										)}
										{!addBank.addBankButton4 &&
										!hasBankFourInfo ? (
											<Grid
												item
												xs={12}
												style={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<Button
													type="button"
													className={classes.button}
													onClick={() =>
														handleAddBank(
															'addBankButton4'
														)
													}
													startIcon={<AddIcon />}
													id={addBankButton4Id}
												>
													{tSettings('addBank4')}
												</Button>
											</Grid>
										) : (
											<>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bankProfileName4Id}
														label={tSettings(
															'bankProfileName4'
														)}
														name="bankProfileName4"
														defaultValue={
															companyInfo?.bankProfileName4
																? companyInfo.bankProfileName4
																: 'Bank Profile 4'
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bank4Id}
														label={tSettings(
															'bank4'
														)}
														name="bank4"
														defaultValue={
															companyInfo?.bank4
																? companyInfo.bank4
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={iban4Id}
														label={tSettings(
															'iban4'
														)}
														name="iban4"
														defaultValue={
															companyInfo?.iban4
																? companyInfo.iban4
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bic4Id}
														label={tSettings(
															'bic4'
														)}
														name="bic4"
														defaultValue={
															companyInfo?.bic4
																? companyInfo.bic4
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={accountName4Id}
														label={tSettings(
															'accountName4'
														)}
														name="accountName4"
														defaultValue={
															companyInfo?.accountName4
																? companyInfo.accountName4
																: null
														}
													/>
												</Grid>
												<Grid item xs={6} />
											</>
										)}
										{!addBank.addBankButton5 &&
										!hasBankFiveInfo ? (
											<Grid
												item
												xs={12}
												style={{
													display: 'flex',
													alignItems: 'center',
												}}
											>
												<Button
													type="button"
													className={classes.button}
													onClick={() =>
														handleAddBank(
															'addBankButton5'
														)
													}
													startIcon={<AddIcon />}
													id={addBankButton5Id}
												>
													{tSettings('addBank5')}
												</Button>
											</Grid>
										) : (
											<>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bankProfileName5Id}
														label={tSettings(
															'bankProfileName5'
														)}
														name="bankProfileName5"
														defaultValue={
															companyInfo?.bankProfileName5
																? companyInfo.bankProfileName5
																: 'Bank Profile 5'
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bank5Id}
														label={tSettings(
															'bank5'
														)}
														name="bank5"
														defaultValue={
															companyInfo?.bank5
																? companyInfo.bank5
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={iban5Id}
														label={tSettings(
															'iban5'
														)}
														name="iban5"
														defaultValue={
															companyInfo?.iban5
																? companyInfo.iban5
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={bic5Id}
														label={tSettings(
															'bic5'
														)}
														name="bic5"
														defaultValue={
															companyInfo?.bic5
																? companyInfo.bic5
																: null
														}
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														variant="outlined"
														margin="normal"
														inputRef={register}
														fullWidth
														id={accountName5Id}
														label={tSettings(
															'accountName5'
														)}
														name="accountName5"
														defaultValue={
															companyInfo?.accountName5
																? companyInfo.accountName5
																: null
														}
													/>
												</Grid>
											</>
										)}

										<Grid
											item
											xs={12}
											className={classes.buttonsContainer}
										>
											<span className={classes.button}>
												<LoadingButton
													type="submit"
													variant="contained"
													color="primary"
													className={classes.button}
													loading={
														updateCompanyInfoApiStatus ===
														'loading'
															? true
															: false
													}
												>
													{tClientFormModal('save')}
												</LoadingButton>
											</span>
											<span className={classes.button}>
												<Button
													variant="contained"
													color="secondary"
													className={classes.button}
													onClick={handleCloseModal}
												>
													{tClientFormModal('cancel')}
												</Button>
											</span>
										</Grid>
									</Grid>
								</form>
							</Paper>
						</Grid>
					</div>
				</Modal>
			)}
		</>
	)
}

export default AccountSettingsModal
